import {
  COLLECTIONS,
  type ActivityLogDoc,
  type AdminDoc,
  type AnnouncementDoc,
  type JobDoc,
  type RegistrationDoc,
  type MessageDoc,
  type TeamMessageDoc,
  type ThreadDoc,
} from '@crew/shared';
import { collection, doc, getDocs, getDocsFromServer, limit, onSnapshot, orderBy, query, where, type DocumentData, type FirestoreError } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAdmin } from '@/auth/AuthProvider';
import { demoStore } from '@/demo/demoStore';
import { IS_DEMO } from '@/demo/demoMode';
import { tsToDate, type Ts } from '@/lib/ts';
import { readerUid } from '@/services/adminApi';
import { getFirebase } from '@/services/firebase';
import { firestoreErrorMessage, type ActivityView, type LiveQuery } from './useActiveJobs';

/** עדכונים לנרשמים, שיחות פרטיות עם נרשמים וצ'אט המנהלים — קריאה חיה; כתיבה רק דרך השרת. */

export type AnnouncementView = AnnouncementDoc<Ts> & { id: string };
export type ThreadView = ThreadDoc<Ts> & { id: string };
export type MessageView = MessageDoc<Ts> & { id: string };
export type TeamMessageView = TeamMessageDoc<Ts> & { id: string };

const withId = <T,>(d: { id: string; data(): DocumentData }) => ({ id: d.id, ...(d.data() as T) });

function useLive<T>(key: string, demo: () => T, firebase: (ok: (d: T) => void, err: (e: FirestoreError) => void) => () => void): LiveQuery<T> {
  const [result, setResult] = useState<LiveQuery<T>>({ state: 'loading' });
  useEffect(() => {
    setResult({ state: 'loading' });
    if (IS_DEMO) {
      const push = () => setResult({ state: 'ready', data: demo() });
      push();
      return demoStore.subscribe(push);
    }
    return firebase(
      (data) => setResult({ state: 'ready', data }),
      (e) => setResult({ state: 'error', message: firestoreErrorMessage(e.code) }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return result;
}

export function useAnnouncements(jobId: string): LiveQuery<AnnouncementView[]> {
  return useLive(
    `announcements:${jobId}`,
    () => demoStore.announcements(jobId),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.jobs, jobId, COLLECTIONS.announcements), orderBy('createdAt', 'desc'), limit(100));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<AnnouncementDoc<Ts>>(d))), err);
    },
  );
}

/** כל השיחות, מהאחרונה שזזה. */
export function useThreads(max = 200): LiveQuery<ThreadView[]> {
  return useLive(
    `threads:${max}`,
    () => demoStore.threads(),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.threads), orderBy('lastMessageAt', 'desc'), limit(max));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<ThreadDoc<Ts>>(d))), err);
    },
  );
}

export function useJobThreads(jobId: string): LiveQuery<ThreadView[]> {
  return useLive(
    `threads:job:${jobId}`,
    () => demoStore.threads().filter((t) => t.jobId === jobId),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.threads), where('jobId', '==', jobId));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<ThreadDoc<Ts>>(d))), err);
    },
  );
}

export function useThread(id: string): LiveQuery<ThreadView | null> {
  return useLive(
    `thread:${id}`,
    () => demoStore.thread(id),
    (ok, err) => {
      const { db } = getFirebase();
      return onSnapshot(doc(db, COLLECTIONS.threads, id), (s) => ok(s.exists() ? { id: s.id, ...(s.data() as ThreadDoc<Ts>) } : null), err);
    },
  );
}

export function useThreadMessages(id: string): LiveQuery<MessageView[]> {
  return useLive(
    `messages:${id}`,
    () => demoStore.threadMessages(id),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.threads, id, COLLECTIONS.messages), orderBy('createdAt', 'asc'), limit(500));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<MessageDoc<Ts>>(d))), err);
    },
  );
}

/** האם יש בשיחה הודעה מהנרשם שהמנהל המחובר עוד לא קרא. */
export function isThreadUnread(t: ThreadView, uid: string): boolean {
  if (t.lastSender !== 'contractor') return false;
  const read = tsToDate(t.readBy?.[uid]);
  const last = tsToDate(t.lastMessageAt);
  return !!last && (!read || read.getTime() < last.getTime());
}

/** 200 ההודעות האחרונות בצ'אט המנהלים, מהישנה לחדשה. */
export function useTeamChat(max = 200): LiveQuery<TeamMessageView[]> {
  return useLive(
    `team:${max}`,
    () => demoStore.teamMessages(),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.teamChat), orderBy('createdAt', 'desc'), limit(max));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<TeamMessageDoc<Ts>>(d)).reverse()), err);
    },
  );
}

/** מתי המנהל המחובר קרא לאחרונה את צ'אט המנהלים (נשמר במסמך המנהל שלו). */
export function useTeamReadAt(): LiveQuery<Date | null> {
  const { user } = useAdmin();
  const uid = readerUid(user);
  return useLive(
    `teamReadAt:${uid}`,
    () => tsToDate(demoStore.teamLastRead()),
    (ok, err) => {
      const { db } = getFirebase();
      return onSnapshot(doc(db, COLLECTIONS.admins, uid), (s) => ok(tsToDate((s.data() as AdminDoc<Ts> | undefined)?.teamChatReadAt)), err);
    },
  );
}

export interface UnreadMessages {
  threads: number;
  team: number;
  total: number;
}

/** מונה להודעות שלא נקראו — לתג בתפריט. */
export function useUnreadMessages(): UnreadMessages {
  const { user } = useAdmin();
  const uid = readerUid(user);
  const threads = useThreads(100);
  const team = useTeamChat(50);
  const readAt = useTeamReadAt();
  const t = threads.state === 'ready' ? threads.data.filter((x) => isThreadUnread(x, uid)).length : 0;
  let m = 0;
  if (team.state === 'ready' && readAt.state === 'ready') {
    const since = readAt.data?.getTime() ?? 0;
    m = team.data.filter((x) => x.authorUid !== uid && (tsToDate(x.createdAt)?.getTime() ?? 0) > since).length;
  }
  return { threads: t, team: m, total: t + m };
}

/** כל יומן הפעילות של עבודה (עד 1000 פעולות) — לדוח PDF. נטען פעם אחת מהשרת. */
export async function fetchFullActivity(jobId: string): Promise<ActivityView[]> {
  if (IS_DEMO) return demoStore.activity(jobId);
  const { db } = getFirebase();
  const q = query(collection(db, COLLECTIONS.activityLog), where('jobId', '==', jobId), orderBy('createdAt', 'desc'), limit(1000));
  const snap = await getDocsFromServer(q);
  return snap.docs.map((d) => withId<ActivityLogDoc<Ts>>(d));
}

export interface RegistrantOption {
  jobId: string;
  jobName: string;
  jobDate: string;
  contractorId: string;
  fullName: string;
  phone: string;
  registrantType: RegistrationDoc['registrantType'];
  workerCount: number;
}

/** כל הנרשמים בעבודות הפעילות — לבחירה בפתיחת שיחה חדשה. נטען כשהחלון נפתח. */
export function useActiveRegistrants(enabled: boolean): LiveQuery<RegistrantOption[]> {
  const [result, setResult] = useState<LiveQuery<RegistrantOption[]>>({ state: 'loading' });
  useEffect(() => {
    if (!enabled) return;
    setResult({ state: 'loading' });
    let cancelled = false;
    const toOption = (j: { id: string; name: string; date: string }, r: RegistrationDoc<Ts> & { id: string }): RegistrantOption => ({
      jobId: j.id,
      jobName: j.name,
      jobDate: j.date,
      contractorId: r.id,
      fullName: r.fullName,
      phone: r.phone,
      registrantType: r.registrantType,
      workerCount: r.workerCount,
    });
    const sort = (list: RegistrantOption[]) => list.sort((a, b) => a.jobDate.localeCompare(b.jobDate) || a.fullName.localeCompare(b.fullName, 'he'));
    if (IS_DEMO) {
      const data = demoStore.jobs('active').flatMap((j) => demoStore.registrations(j.id).map((r) => toOption(j, r)));
      setResult({ state: 'ready', data: sort(data) });
      return;
    }
    const { db } = getFirebase();
    (async () => {
      const jobs = await getDocs(query(collection(db, COLLECTIONS.jobs), where('status', '==', 'active'), orderBy('date', 'asc'), limit(100)));
      const lists = await Promise.all(
        jobs.docs.map(async (j) => {
          const job = { id: j.id, ...(j.data() as JobDoc<Ts>) };
          const regs = await getDocs(collection(db, COLLECTIONS.jobs, j.id, COLLECTIONS.registrations));
          return regs.docs.map((r) => toOption(job, withId<RegistrationDoc<Ts>>(r)));
        }),
      );
      if (!cancelled) setResult({ state: 'ready', data: sort(lists.flat()) });
    })().catch((e: FirestoreError) => !cancelled && setResult({ state: 'error', message: firestoreErrorMessage(e.code) }));
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return result;
}
